import { useState } from 'react';
import { GraphQLError } from 'graphql';
import { useTranslation } from 'react-i18next';
import { Formik } from 'formik';
import { useHistory } from 'react-router';
import { Box, Flex } from 'reflexbox';

import { Button, Input, Label } from '@treams/treams-ui';

import PublicForm from 'packages/kakapo-ui-kit/components/forms/layouts/PublicForm';
import GraphQLErrorMessage from 'packages/kakapo-ui-kit/components/forms/errors/GraphQLErrorMessage';
import * as graphql from 'generated/graphql';

export default function PasswordChallenge(props: {
	login: string;
	context: string;
	error?: GraphQLError;
	challenge: graphql.PasswordChallenge;
	onCancel: () => void;
	onResponse: (response: graphql.UpdateSessionResponse) => void;
	isPrefill?: boolean;
}) {
	const { t } = useTranslation();
	const history = useHistory();

	const { login, context, challenge } = props;

	const [commit] = graphql.usePasswordChallengeMutation();
	const [commitResend] = graphql.useResendTemporaryPasswordMutation();

	const [error, setError] = useState<GraphQLError | undefined>(undefined);

	const resend = async () => {
		try {
			await commitResend({
				variables: {
					input: {
						login: props.login,
					},
				},
			});

			history.push('/check-inbox');
		} catch (errors) {
			setError(errors.graphQLErrors[0]);
		}
	};

	if (
		error &&
		(error?.extensions?.error as any)?.key === 'errors.sessions.temporaryPasswordRequired'
	) {
		return (
			<>
				<GraphQLErrorMessage error={error || props.error} />
				<Button title={t('common.next')} onClick={() => resend()} />
			</>
		);
	}

	return (
		<Formik
			initialValues={{
				login,
				password: '',
			}}
			onSubmit={async ({ password }) => {
				try {
					const { data } = await commit({
						variables: {
							input: {
								response: {
									context,
									password: {
										username: challenge.username,
										password,
									},
								},
							},
						},
					});

					props.onResponse(data!.updateSession);
				} catch (errors) {
					setError(errors.graphQLErrors[0]);
				}
			}}
		>
			<PublicForm>
				<GraphQLErrorMessage error={error || props.error} />

				{props.isPrefill ? null : (
					<Box mb={3}>
						<Label mb={2}>{t('common.userNameOrEmail')}:</Label>
						<Flex>
							<Input.Formik name="login" autoFocus disabled flexGrow={1} flexShrink={1} mr={2} />
							<Button
								small
								type="secondary"
								title={t('auth.login.password.changeUsername')}
								onClick={props.onCancel}
							/>
						</Flex>
					</Box>
				)}
				<Box mb={3}>
					<Label mb={2}>{t('common.password')}:</Label>
					<Input.Formik type="password" name="password" autoFocus />
				</Box>
				<Button.Formik mb={3} title={t('common.next')} disableSuccess={Boolean(error)} />
				<Button type="secondary" link="/forgot-password" title={t('common.forgottenPassword')} />
			</PublicForm>
		</Formik>
	);
}
